import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './AuthPageNew.css';

function AuthPageNew({ mode = 'login' }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { data, error: loginError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    setLoading(false);

    if (loginError) {
      setError(loginError.message);
      return;
    }

    if (data.user) {
      // Navigate to checker - the onAuthStateChange listener in App.js
      // will update the user state automatically
      navigate('/checker');
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');

    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
    });

    setLoading(false);

    if (signUpError) {
      setError(signUpError.message);
      return;
    }

    if (data.user) {
      // Create profile via API endpoint (uses service role to bypass RLS)
      try {
        const profileResponse = await fetch('/api/create-profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: data.user.id,
            email: email
          })
        });

        if (!profileResponse.ok) {
          console.error('Failed to create profile:', await profileResponse.text());
        }
      } catch (profileError) {
        console.error('Failed to create profile:', profileError);
      }

      // Send welcome email
      fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: email,
          type: 'welcome',
          userName: null
        })
      }).catch(err => console.error('Failed to send welcome email:', err));

      // Navigate to checker - the onAuthStateChange listener in App.js
      // will update the user state automatically
      navigate('/checker');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/?reset=true`,
    });

    setLoading(false);

    if (resetError) {
      setError(resetError.message);
      return;
    }

    setSuccessMessage('Check your email for a password reset link');
  };

  const isLogin = mode === 'login';
  const isSignup = mode === 'signup';
  const isForgot = mode === 'forgot';

  return (
    <div className="auth-page-new">
      <div className="auth-container-new">
        <Link to="/" className="auth-logo">Mindmark</Link>

        <div className="auth-card-new">
          <h1 className="auth-title">
            {isLogin && 'Welcome back'}
            {isSignup && 'Create your account'}
            {isForgot && 'Reset your password'}
          </h1>
          <p className="auth-subtitle">
            {isLogin && 'Log in to access the compliance checker'}
            {isSignup && 'Get started with 3 free compliance checks'}
            {isForgot && "We'll send you a link to reset your password"}
          </p>

          <form onSubmit={isLogin ? handleLogin : isSignup ? handleSignUp : handleForgotPassword}>
            <div className="auth-field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>

            {!isForgot && (
              <div className="auth-field">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder={isSignup ? 'At least 6 characters' : 'Enter your password'}
                  required
                />
              </div>
            )}

            {isSignup && (
              <div className="auth-field">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  placeholder="Confirm your password"
                  required
                />
              </div>
            )}

            {isSignup && (
              <div className="auth-checkbox-field">
                <label className="auth-checkbox-label">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => { setAgreedToTerms(e.target.checked); setError(''); }}
                  />
                  <span className="auth-checkbox-text">
                    I agree to the <Link to="/terms" target="_blank">Terms of Service</Link> and <Link to="/privacy" target="_blank">Privacy Policy</Link>
                  </span>
                </label>
              </div>
            )}

            {error && (
              <div className="auth-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {error}
              </div>
            )}

            {successMessage && (
              <div className="auth-success">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                {successMessage}
              </div>
            )}

            <button type="submit" className="auth-submit" disabled={loading || (isSignup && !agreedToTerms)}>
              {loading ? (
                <>
                  <span className="spinner"></span>
                  {isLogin && 'Logging in...'}
                  {isSignup && 'Creating account...'}
                  {isForgot && 'Sending...'}
                </>
              ) : (
                <>
                  {isLogin && 'Log in'}
                  {isSignup && 'Create account'}
                  {isForgot && 'Send reset link'}
                </>
              )}
            </button>
          </form>

          <div className="auth-links">
            {isLogin && (
              <>
                <Link to="/forgot-password" className="auth-link">Forgot password?</Link>
                <span className="auth-divider">|</span>
                <Link to="/signup" className="auth-link">Create an account</Link>
              </>
            )}
            {isSignup && (
              <>
                <span className="auth-link-text">Already have an account?</span>
                <Link to="/login" className="auth-link">Log in</Link>
              </>
            )}
            {isForgot && (
              <Link to="/login" className="auth-link">Back to login</Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthPageNew;
